import React, { FC } from "react";
import { Box, Chip, IconButton, Link as MuiLink, Paper, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import {
  LEG_LABEL,
  LiveBoardTrip,
  STALE_MIN,
  SILENT_MIN,
  fmtAgo,
  fmtLocal,
  fmtRel,
  hasBackhaul,
  isLate,
  isPotLate,
  isSilent,
  isStale,
  isOverdue,
  missing,
  notArrived,
  targetOf,
  tripHref,
} from "map/liveBoard";
import { BOARD_COLORS, colorFor } from "./palette";

/**
 * Everything known about one trip: the flags, the timeline so far, who and what is on it, and
 * which of its coordinates the board refused to draw.
 */
const TripDetailCard: FC<{ trip: LiveBoardTrip; onClose: () => void }> = ({ trip: t, onClose }) => {
  const color = colorFor(t);
  const tgt = targetOf(t);

  const tags: [string, string][] = [];
  if (isLate(t)) {
    tags.push([
      BOARD_COLORS.late,
      isOverdue(t)
        ? "late · past pickup, not started"
        : notArrived(t)
        ? `late · not arrived +${t.late_minutes}m`
        : `late +${t.late_minutes}m`,
    ]);
  }
  if (isPotLate(t)) tags.push([BOARD_COLORS.warn, "potential late"]);
  if (isSilent(t)) tags.push([BOARD_COLORS.warn, `silent ${SILENT_MIN}m+`]);
  if (missing(t).length) tags.push([BOARD_COLORS.warn, `no ${missing(t).join(" / ")}`]);
  if (t.leg !== "pending" && isStale(t)) tags.push([BOARD_COLORS.warn, `GPS > ${STALE_MIN}m`]);
  if (hasBackhaul(t)) tags.push([BOARD_COLORS.backhaul, "backhaul opportunity"]);
  if (t.bad_coords?.length) tags.push([BOARD_COLORS.warn, "invalid coordinates"]);

  const rows: [string, string][] = [
    ["Status", `${t.trip_status}${t.dispatch_status !== "Dispatched" ? ` · ${t.dispatch_status}` : ""}`],
    ["Leg", LEG_LABEL[t.leg]],
    ["Client", `${t.client}${t.trip_type ? ` · ${t.trip_type}` : ""}`],
    ["Hub", `${t.hub}${t.hub_state && !t.hub?.includes(t.hub_state) ? `, ${t.hub_state}` : ""}`],
    ["Train", t.trainNumber || "—"],
    [
      "Driver",
      t.driver_name
        ? `${t.driver_name}${t.driver_employee_number ? ` · #${t.driver_employee_number}` : ""}${
            t.driver_cell ? ` · ${t.driver_cell}` : ""
          }`
        : "not assigned",
    ],
    [
      "Vehicle",
      t.vehicle_unit
        ? `Unit ${t.vehicle_unit} · ${t.vehicle_description || ""}${t.vehicle_plate ? ` · ${t.vehicle_plate}` : ""}`
        : "not assigned",
    ],
    ["Pickup", `${t.pickup_name}${t.pickup_city ? ` · ${t.pickup_city}, ${t.pickup_state}` : ""}`],
    ["Pickup time", `${fmtLocal(t.pickup_local)} ${t.trip_tz} · ${fmtRel(t.minutes_to_pickup)}`],
    [
      "Dropoff",
      `${t.dropoff_name}${t.dropoff_city ? ` · ${t.dropoff_city}, ${t.dropoff_state}` : ""}${
        t.planned_miles ? ` · ${t.planned_miles} mi` : ""
      }`,
    ],
  ];
  if (t.begin_trip_utc) rows.push(["Begin trip", `${fmtLocal(t.begin_trip_utc)} UTC`]);
  if (t.arrived_pickup_utc) {
    rows.push([
      "Arrived pickup",
      `${fmtLocal(t.arrived_pickup_utc)} UTC${t.is_late ? ` · ${t.late_minutes} min late` : ""}`,
    ]);
  }
  if (t.pickup_completed_utc) rows.push(["Depart w/ crew", `${fmtLocal(t.pickup_completed_utc)} UTC`]);
  if (t.crew_dropped_utc) rows.push(["Crew dropped", `${fmtLocal(t.crew_dropped_utc)} UTC`]);
  rows.push(["Last event", t.last_event ? `${t.last_event} · ${fmtAgo(t.minutes_since_last_event)}` : "none yet"]);
  if (t.leg !== "pending") {
    rows.push([
      "Vehicle GPS",
      t.vehicle_gps_age_min == null
        ? "no GPS row"
        : `${fmtAgo(t.vehicle_gps_age_min)}${
            t.vehicle_stopped_min > 1 ? ` · stopped ${fmtRel(-t.vehicle_stopped_min).replace(" ago", "")}` : " · moving"
          }`,
    ]);
  }
  if (t.leg === "pending" && t.drive_minutes_to_pickup != null) {
    rows.push([
      "Drive to pickup",
      `${t.drive_minutes_to_pickup} min (${
        t.drive_minutes_source === "trip" ? "trip" : "point-to-hub table"
      }) · leave by ${fmtLocal(t.must_start_by_utc)} UTC`,
    ]);
  }
  if (hasBackhaul(t)) rows.push(["Backhaul", `${t.backhaul_hubs} within 15 min of dropoff`]);
  rows.push([
    "Pin",
    t.leg === "pending"
      ? "pickup point"
      : `${(t.current_loc_source || "").replace("_", " ")}${tgt ? ` → ${tgt.kind}` : ""}`,
  ]);
  if (t.current_lat != null) rows.push(["Coords", `${(+t.current_lat).toFixed(5)}, ${(+t.current_lon).toFixed(5)}`]);
  if (t.bad_coords?.length) {
    rows.push(["Invalid coords", `${t.bad_coords.join(" · ")} · outside the continental US, not drawn`]);
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderLeft: `4px solid ${color}`, maxHeight: 340, overflowY: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color }}>Trip #{t.tripID}</Typography>
        <Chip size="small" label={LEG_LABEL[t.leg]} sx={{ bgcolor: color, color: "#fff", height: 18, fontSize: 10 }} />
        <IconButton size="small" sx={{ ml: "auto" }} onClick={onClose} aria-label="Close details" title="Close (Esc)">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <MuiLink
        component={Link}
        to={tripHref(t)}
        target="_blank"
        rel="noopener"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, fontSize: 12, mt: 0.25 }}
      >
        Open in Flow <OpenInNewIcon sx={{ fontSize: 12 }} />
      </MuiLink>
      {tags.length ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
          {tags.map(([c, label]) => (
            <Chip
              key={label}
              size="small"
              label={label}
              variant="outlined"
              sx={{ height: 18, fontSize: 10, color: c, borderColor: c }}
            />
          ))}
        </Box>
      ) : null}
      <Box
        sx={{
          mt: 0.75,
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          columnGap: 1,
          rowGap: 0.25,
          fontSize: 12,
        }}
      >
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <Box sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>{k}</Box>
            <Box sx={{ fontFamily: k.indexOf("oords") > 0 ? "monospace" : undefined, wordBreak: "break-word" }}>{v}</Box>
          </React.Fragment>
        ))}
      </Box>
    </Paper>
  );
};

export default TripDetailCard;
